import React from "react";
import PropTypes from "prop-types";
import classnames from "classnames";
import noop from "shared/util/noop";
import {
	CODE_LENGTH,
	initialInputs,
	isValidCode,
	inputIndexIsInBounds,
	getInputIndex,
	getDigits,
} from "./helpers";

import "./CodeInput.scss";

function CodeInput({ handleDigitChange, showError }) {
	// attach an array of refs to a ref so the refs are not recreated every render
	const inputRefs = React.useRef(initialInputs.map(React.createRef));
	const [inputs, setInputs] = React.useState(initialInputs);

	const handleChange = React.useCallback((e) => {
		const currentInputIndex = getInputIndex(e);

		// handle ios code autofill which fires a change event in the first input instead of paste
		const digits = getDigits(e.target.value);

		if (digits.length === 6 && currentInputIndex === 0) {
			const updatedInputs = inputs.map((input, i) => {
				return input.index >= currentInputIndex ?
					{ ...input, value: digits[i] || input.value }
					: input;
			});

			setInputs(updatedInputs);
			handleDigitChange(updatedInputs);
			return;
		}

		// if there is more than one digit take the last one
		// this can happen if the user clicks into an input so the cursor
		// is adjacent to the number
		const digit = digits.length > 1 ? digits[digits.length - 1] : digits;
		const nextInputIndex = currentInputIndex + 1;
		const updatedInputs = inputs.map((input) => {
			return input.index === currentInputIndex ? { ...input, value: digit } : input;
		});

		setInputs(updatedInputs);
		handleDigitChange(updatedInputs);

		// only move to the next input if there was a digit
		// (if one was deleted then handleKeyDown backspace case will jump to the previous input)
		if (inputIndexIsInBounds(nextInputIndex) && digit) {
			const nextInput = inputRefs.current[nextInputIndex].current;
			nextInput.focus();
		}
	}, [inputs, handleDigitChange]);

	const handleKeyDown = React.useCallback((e) => {
		const currentInputIndex = getInputIndex(e);
		const previousInputIndex = currentInputIndex - 1;
		const nextInputIndex = currentInputIndex + 1;
		const digit = e.target.value;

		const focusInputAt = (index) => {
			if (!inputIndexIsInBounds(index)) {
				return;
			}

			const input = inputRefs.current[index].current;
			input.focus();
		};

		switch (e.key) {
			case "Backspace": {
				e.preventDefault();

				let updatedInputs = inputs.map((input) => {
					return (input.index === currentInputIndex) ? { ...input, value: "" } : input;
				});

				if (!digit) {
					// if there was no digit here clear the previous one
					updatedInputs = inputs.map((input) => {
						return input.index === currentInputIndex - 1 ? { ...input, value: "" } : input;
					});
				}

				setInputs(updatedInputs);
				handleDigitChange(updatedInputs);

				if (!digit) {
					focusInputAt(previousInputIndex);
				}

				break;
			}

			case "ArrowLeft": {
				e.preventDefault();
				focusInputAt(previousInputIndex);
				break;
			}

			case "ArrowRight": {
				e.preventDefault();
				focusInputAt(nextInputIndex);
				break;
			}
			default:
				break;
		}
	}, [inputs, handleDigitChange]);

	const handleFocus = React.useCallback((e) => {
		const currentInputIndex = getInputIndex(e);

		inputRefs.current[currentInputIndex].current.placeholder = "";
	}, []);

	const handleBlur = React.useCallback((e) => {
		const currentInputIndex = getInputIndex(e);

		inputRefs.current[currentInputIndex].current.placeholder = "0";
	}, []);

	const handlePaste = React.useCallback((e) => {
		e.preventDefault();

		// window.clipboardData for IE
		const code = ((e.clipboardData || window.clipboardData).getData("text") || "").trim().slice(0, CODE_LENGTH);

		if (isValidCode(code)) {
			const digits = code.split("");
			const updatedInputs = inputs.map((input, index) => ({ ...input, value: digits[index] }));

			setInputs(updatedInputs);
			handleDigitChange(updatedInputs);
		}
	}, [inputs, handleDigitChange]);

	const digitClassNames = classnames("code-inputs-digit", { "code-inputs-digit-error": showError });


	return (
		<div className="code-inputs">
			{showError && <p className="code-inputs-error">Invalid Code. Please try again.</p>}
			<div className="code-inputs-digits">
				{inputs.map(({ value, index }) => (
					<input
						key={`code-${index}`}
						type="number"
						pattern="\d*"
						inputMode="numeric"
						className={digitClassNames}
						placeholder="0"
						ref={inputRefs.current[index]}
						data-index={index}
						value={value}
						min={0}
						max={9}
						onKeyDown={handleKeyDown}

						// use onInput because onChange will not fire
						// when the value is changed but the input is not blurred
						// (e.g the digit was selected and then changed)
						onInput={handleChange}
						onChange={noop}
						onFocus={handleFocus}
						onBlur={handleBlur}
						aria-required
						aria-labelledby={`code input ${index}`}
						onPaste={handlePaste}
					/>
				))}
			</div>
		</div>
	);
}

CodeInput.propTypes = {
	/**
     * handleDigitChange is called when a digit changes.
     *
     * @param {{ index: Number, value: String }[]} inputs All of the input objects.
     */
	handleDigitChange: PropTypes.func,
	showError: PropTypes.bool,
};

CodeInput.defaultProps = {
	handleDigitChange: noop,
	showError: false,
};


export default CodeInput;



// WEBPACK FOOTER //
// ./src/shared/components/CodeInput/index.jsx